import {compose} from "mu-lambda";
import * as React from "react";

import {
    withActionScheduler,
    type ITimeout,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";

import withSandboxContext, {
    type WithoutSandboxContext,
    type WithSandboxContextProps,
} from "#testing/sandbox/with-sandbox-context.tsx";

import {
    areEqual,
    DEFAULT_PAGE_SETTINGS,
    getPageSettings,
    previewSettings,
    updatePageSettingsInternal,
    type PageSettings,
    type PartialPageSettings,
} from "./page-settings.ts";

export type PageDisplayUpdateFn = (
    settings: PartialPageSettings,
    reset?: boolean | null | undefined,
) => void;

export type PageDisplayContextType = {
    settings: Readonly<PageSettings>;
    update: PageDisplayUpdateFn;
};

/**
 * Internal context used to connect the PageDisplay components with
 * pageSettings.
 *
 * NOTE: We only export this to be used by PageDisplay and PageDisplayProvider.
 * Please avoid using it directly (unless you need to test any particular
 * scenario).
 * @private
 */
export const PageDisplayContext: React.Context<PageDisplayContextType> =
    React.createContext<PageDisplayContextType>({
        settings: getPageSettings(),
        update: () => {
            /* NOOP */
        },
    });
PageDisplayContext.displayName = "PageDisplayContext";

type Props = {
    children: React.ReactNode;
} & WithActionSchedulerProps &
    WithSandboxContextProps;

type State = {
    settings: Readonly<PageSettings>;
};

/**
 * The PageDisplayContainer component is the glue that connects PageDisplay and
 * PageDisplayProvider. Internally uses ReactContext.Provider to pass data to
 * all the consumers that are descendants of this container. It also uses the
 * PageSettings object to synchronize page settings data in SSR and CSR.
 *
 * NOTE: This component is meant to be used as the PageDisplay and
 * PageDisplayProvider connector. For more details about its usage, please check
 * `app-shell-package/app.jsx`.
 *
 * @example
 * <PageDisplayContainer>
 *  <ChildComponent />
 * </PageDisplayContainer>
 */
class PageDisplayContainer extends React.Component<Props, State> {
    _mounted: boolean;
    _pendingSettings: Readonly<PageSettings> | null | undefined;
    _pendingSetState: ITimeout | null | undefined;

    constructor(props: Props) {
        super(props);
        this._mounted = false;
        this.state = {
            settings: getPageSettings(),
        };
        this._pendingSettings = null;
    }

    componentDidMount() {
        this._mounted = true;
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    /**
     * Get the current settings for this PageDisplayContainer instance.
     *
     * The current settings for an instance are:
     *  - If we are waiting to update local state, those pending settings,
     *  - Else if we're in a fixture, our local state,
     *  - Otherwise, the global page settings.
     */
    _getCurrentSettings: () => Readonly<PageSettings> = () =>
        this._pendingSettings ||
        (this.props.isInFixture ? this.state.settings : getPageSettings());

    _fireStateUpdate: () => void = () => {
        const {schedule, log} = this.props;

        /**
         * If we have already queued up a state update, let's reset it.
         * We can reuse one that already fired.
         * It's going to apply the latest requested settings, so we don't need
         * to do anything more.
         * This prevents us having lots of competing state calls that cause
         * cyclic re-rendering.
         */
        if (this._pendingSetState != null) {
            this._pendingSetState.set();
            return;
        }

        /**
         * Otherwise, let's queue one.
         */
        this._pendingSetState = schedule.timeout(() => {
            // Capture settings to apply and clear pending state tracking.
            const settingsUpdate = this._getCurrentSettings();

            /**
             * If we aren't mounted or the settings changes we've accumulated
             * won't actually change anything, we can just stop.
             */
            // If we aren't mounted, no point in doing any more.
            if (
                !this._mounted ||
                areEqual(this.state.settings, settingsUpdate)
            ) {
                return;
            }
            this._pendingSettings = null;

            // Update the state with the settings.
            this.setState({settings: settingsUpdate});

            // Let's log this change - the sandbox log is a noop if we're not
            // in the sandbox.
            log("Page settings updated", settingsUpdate);
        }, 0);
    };

    _updateGlobalPageSettings: (
        settings: Readonly<PartialPageSettings>,
        reset: boolean,
    ) => void = (
        settings: Readonly<PartialPageSettings>,
        reset: boolean,
    ): void => {
        if (this.props.isInFixture) {
            return;
        }
        // If enabled, reset the page settings here to revert some
        // settings that might not be used at this time.
        // e.g. footer should be true if not defined in a new PageDisplay
        //      instance
        // eslint-disable-next-line no-restricted-syntax
        updatePageSettingsInternal(settings, reset);
    };

    updateSettings: (settings: PartialPageSettings, reset: boolean) => void = (
        settings: PartialPageSettings,
        reset: boolean,
    ) => {
        /**
         * We have to be smart or we cause odd flipflopping (for example, we set
         * to defaults in one component then set a title in another; rerendering
         * causes that to keep changing over and over and over, causing a
         * flickering title and other re-rendering overhead).
         *
         * So, we get our current settings and determine if this request even
         * changes them. If it doesn't, we don't need to do any more work.
         */
        const latestSettings = this._getCurrentSettings();

        /**
         * Now, let's see what these new settings do to the state of our
         * page settings.
         */
        const newPageSettings = previewSettings(
            settings,
            reset ? DEFAULT_PAGE_SETTINGS : latestSettings,
        );
        if (areEqual(newPageSettings, latestSettings)) {
            // No changes. Nothing to do. Just return.
            return;
        }

        /**
         * We need to track this in our pending settings so that when we update
         * our state, we have the appropriate values, even if we're in the
         * sandbox and aren't tracking settings in the global set.
         *
         * Then we fire a state update to make sure that these new settings
         * get applied at the earliest convenience and update the global
         * page settings (if we're not in a fixture).
         */
        this._pendingSettings = newPageSettings;
        this._updateGlobalPageSettings(settings, reset);
        this._fireStateUpdate();
    };

    render(): React.ReactNode {
        const {children} = this.props;

        return (
            <PageDisplayContext.Provider
                value={{
                    settings: this.state.settings,
                    update: (settings, reset) =>
                        this.updateSettings(settings, !!reset),
                }}
            >
                {children}
            </PageDisplayContext.Provider>
        );
    }
}

type ExportProps = WithoutSandboxContext<
    WithoutActionScheduler<
        JSX.LibraryManagedAttributes<
            typeof PageDisplayContainer,
            React.ComponentProps<typeof PageDisplayContainer>
        >
    >
>;
export default compose(
    withActionScheduler,
    withSandboxContext,
)(PageDisplayContainer) as React.ComponentType<ExportProps>;
